/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Renderer from "App/renderer";
import ModelStatistics from "App/model_statistics";
import TextureDataHandler from "App/utils/textures/texture_data_handler";
import ResolveResolutionWorkerManager from "App/utils/textures/resolve_resolution_worker_manager";
import CrunchDecodeWorkerManager from "App/utils/textures/crunch_decode_worker_manager";
import RenderObject from "App/geometry/objects/object";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";
import IScene from "js_interface/src/scene_interface";

export default
class TexturesManager {

    private _sceneAPI: IScene;
    private _renderer: Renderer;
    private _statistics: ModelStatistics;

    private _textureDataHandlers: {[key: string]: TextureDataHandler;} = {};

    private _workerManager: ResolveResolutionWorkerManager;
    private _crunchDecoderWorkerManager: CrunchDecodeWorkerManager;

    private _paused: boolean = false;

    private _switching: any = {
        requests: []
    };

    private _gpuUploadsLimited: boolean = true;
    private _textureResolutionLimit: number = 16384;
    private _timeReservedForDataUpdatePct: number = 10;

    constructor(aSceneAPI: IScene, aRenderer: Renderer, aStatistics: ModelStatistics) {
        this._sceneAPI = aSceneAPI;
        this._renderer = aRenderer;
        this._statistics = aStatistics;

        this._workerManager = new ResolveResolutionWorkerManager(aSceneAPI, this);
        this._crunchDecoderWorkerManager = new CrunchDecodeWorkerManager(aSceneAPI, this);
    }

    public getGpuUploadsPerFrameIsLimited() {
        return this._gpuUploadsLimited;
    }

    public setLimitGpuUploadsPerFrame(aValue) {
        this._gpuUploadsLimited = aValue;
    }

    //TODO: TexturesManager.TextureResolutionLimit = SceneAPI.RenderSettingsModule.TEXTURE_RESOLUTION_MAX;
    public setTextureResolutionLimit(aValue) {
        this._textureResolutionLimit = aValue;
    }

    public getTexturesResolutionLimit() {
        return this._textureResolutionLimit;
    }

    public findTexture(texDataUID): TextureDataHandler {
        let textureDataHandler = this._textureDataHandlers[texDataUID];
        if (textureDataHandler !== undefined) {
            return textureDataHandler;
        }

        return null;
    }

    public getRenderer(): Renderer {
        return this._renderer;
    }

    public addShape(aShape: RenderObject) {
        if (!aShape.hasTexture()) {
            return;
        }

        let texture = aShape.appearance.texture;

        let texDataUID = texture.getTextureDataUID();

        // extend the uid with texturenode uid because when same texture is placed in more bins, the we need two textures in fact
        let renderGraphObject = <ShapeBatchNode>aShape.getRenderGraphObject();
        if (renderGraphObject) {
            let textureNode = renderGraphObject.findTextureNode();
            if (textureNode) {
                texDataUID += "-" + textureNode.getUID();
            }
        }

        let textureDataHandler = this.findTexture(texDataUID);
        if (!textureDataHandler) {
            textureDataHandler = new TextureDataHandler(this._sceneAPI, texDataUID, this, texture, aShape);
            this._textureDataHandlers[texDataUID] = textureDataHandler;
            this._workerManager.addTextureDataHandler(textureDataHandler);
        }

        textureDataHandler.attachShape(aShape);

        this._workerManager.attachShapeBBox(
            textureDataHandler.getUID(),
            aShape.getUID(),
            aShape.getBoundingBoxMin(),
            aShape.getBoundingBoxMax()
        );
    };

    public updateShapeBBox(aShape) {
        if (!aShape.hasTexture()) {
            return;
        }

        let textureDataHandler = aShape.getTextureDataHandler();
        textureDataHandler.updateShapeBBox(aShape);

        this._workerManager.updateShapeBBox(
            textureDataHandler.getUID(),
            aShape.getUID(),
            aShape.getBoundingBoxMin(),
            aShape.getBoundingBoxMax()
        );
    };

    public removeShape(aShape) {
        if (!aShape.hasTexture()) {
            return;
        }

        let textureDataHandler = aShape.getTextureDataHandler();
        if (!textureDataHandler) {
            return;
        }

        textureDataHandler.detachShape(aShape);

        this._workerManager.detachShapeBBox(textureDataHandler.getUID(), aShape.getUID());

        if (textureDataHandler.isEmpty()) {
            this._workerManager.removeTextureDataHandler(textureDataHandler);

            if (this._textureDataHandlers[textureDataHandler.getUID()] !== undefined) {
                delete this._textureDataHandlers[textureDataHandler.getUID()];
            }
        }
    };

    public recomputeTextureLOD() {
        this._workerManager.recomputeTextureLOD();
    };

    public viewpointChanged() {
        this._workerManager.recomputeTextureLOD();
    };

    public setViewFrustumCullingForTexturesSwitching(aValue) {
        this._workerManager.setViewFrustumCullingForTexturesSwitching(aValue);
    };

    public limitTexturesResolution(aValue) {
        this._workerManager.limitTexturesResolution(aValue);
    };

    public setGPUTexturesMemLimit(aLimitInMB) {
    };

    public pauseTexturesSwitching(aValue) {
        this._paused = aValue;
    };

    public decodeCrunchTexture(aUID, aRequestedLevel, aTileIndex, aArrayBuffer) {
        this._crunchDecoderWorkerManager.decodeTexture(aUID, aRequestedLevel, aTileIndex, aArrayBuffer);
    };

    public textureDecoded(aMessage) {
        let textureDataHandler = this.findTexture(aMessage.uid);
        if (textureDataHandler) {
            textureDataHandler.crunchTextureDecoded(aMessage);
        }
    };

    public switchPrioritiesRecomputed(aMessage) {
        this._switching.requests = aMessage.computedValues;
        this._sceneAPI.viewModeModule.scheduleRedraw();
    };

    public tick(aView) {
        if (aView.isMoving() || this._paused) {
            this._sceneAPI.viewModeModule.scheduleRedraw();
            return;
        }

        let needRedraw = false;
        this._statistics.setTextureSwitchStatistics(0, 0);
        this._timeReservedForDataUpdatePct = this._renderer.getCurrentFrame() * 0.1; // 10% of frame time is reserved to make planet updates
        let start;

        for (let i = 0; i < this._switching.requests.length; i++) {
            let textureDataHandler = this._textureDataHandlers[this._switching.requests[i].UID];
            if (textureDataHandler !== undefined) {
                textureDataHandler.requestResolution(this._switching.requests[i]);
                // measure time only when something happen
                start = performance.now();
                if (textureDataHandler.resolve(this._statistics)) {
                    needRedraw = true;
                    this._timeReservedForDataUpdatePct -= performance.now() - start;
                }
            }

            if (this._gpuUploadsLimited && this._timeReservedForDataUpdatePct < 0.0) {
                this._sceneAPI.viewModeModule.scheduleRedraw();
                break;
            }
        }

        if (needRedraw) {
            this._statistics.setDirty();
            this._sceneAPI.viewModeModule.scheduleRedraw();
        }
    };
}